<template>
  <div class="tf-card">
    <header class="tf-header">
      <div>
        <h5 class="tf-title">Circulação entre locais</h5>
        <p class="tf-subtitle">
          Quem foi de onde pra onde — top rotas por volume.
          {{ flow.unique_tags || 0 }} {{ flow.unique_tags === 1 ? 'local' : 'locais' }}
          envolvido{{ flow.unique_tags === 1 ? '' : 's' }} em
          {{ flow.total_transitions || 0 }} transi{{ flow.total_transitions === 1 ? 'ção' : 'ções' }}.
        </p>
      </div>
      <div class="tf-toggle" v-if="hasData">
        <button
          v-for="opt in viewOptions"
          :key="opt.value"
          type="button"
          class="tf-toggle-btn"
          :class="{ 'tf-toggle-btn-active': view === opt.value }"
          @click="view = opt.value"
        >
          <i :class="opt.icon"></i>
          {{ opt.label }}
        </button>
      </div>
    </header>

    <div v-if="!hasData" class="tf-empty">
      <i class="bx bx-walk tf-empty-icon"></i>
      <p>Sem circulação entre locais ainda</p>
      <small>As rotas aparecem assim que houver pessoas que passam por mais de um local.</small>
    </div>

    <!-- Insights narrativos: padrões de comportamento entre locais.
         Renderiza ANTES das views pra dar contexto qualitativo antes do
         bloco quantitativo (cards/matriz). -->
    <div v-if="hasData && insights.length > 0" class="tf-insights">
      <div
        v-for="(ins, idx) in insights"
        :key="ins.type + '-' + ins.source + '-' + (ins.target || '') + '-' + idx"
        class="tf-insight"
        :class="`tf-insight-${ins.type}`"
      >
        <span class="tf-insight-pct">{{ ins.pct }}%</span>
        <span class="tf-insight-text">
          <template v-if="ins.type === 'next_destination'">
            das pessoas em <strong>{{ ins.source }}</strong>
            visitaram em seguida <strong>{{ ins.target }}</strong>
            <span class="tf-insight-meta">gap médio {{ formatGap(ins.avg_gap_minutes) }}</span>
          </template>
          <template v-else-if="ins.type === 'co_visit'">
            das pessoas em <strong>{{ ins.source }}</strong>
            também passaram por <strong>{{ ins.target }}</strong>
            <span class="tf-insight-meta">em até {{ ins.window_minutes }} min · médio {{ formatGap(ins.avg_gap_minutes) }}</span>
          </template>
          <template v-else-if="ins.type === 'loop_back'">
            das pessoas em <strong>{{ ins.source }}</strong>
            voltaram pra <strong>{{ ins.source }}</strong> depois
            <span class="tf-insight-meta">{{ ins.count }} de {{ ins.total_at_source }} visitantes</span>
          </template>
        </span>
      </div>
    </div>

    <!-- View 1: Cards de transição (default) -->
    <div v-if="hasData && view === 'cards'" class="tf-cards-grid">
      <div
        v-for="(link, idx) in topLinks"
        :key="link.source + '→' + link.target"
        class="tf-route"
        :style="rouletteVars(link)"
      >
        <div class="tf-route-line">
          <div class="tf-node tf-node-source">
            <span class="tf-node-label" :title="link.source">{{ link.source }}</span>
          </div>
          <div class="tf-arrow">
            <svg viewBox="0 0 60 16" preserveAspectRatio="none">
              <defs>
                <linearGradient :id="`tf-grad-${idx}`" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" :stop-color="colorForName(link.source)" stop-opacity="0.65"/>
                  <stop offset="100%" :stop-color="colorForName(link.target)" stop-opacity="0.65"/>
                </linearGradient>
              </defs>
              <path
                d="M2 8 L48 8 M44 4 L52 8 L44 12"
                fill="none"
                :stroke="`url(#tf-grad-${idx})`"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="tf-volume">{{ link.value }}</span>
          </div>
          <div class="tf-node tf-node-target">
            <span class="tf-node-label" :title="link.target">{{ link.target }}</span>
          </div>
        </div>
        <div class="tf-route-foot">
          <span class="tf-route-pct">{{ percentOf(link) }}% do fluxo</span>
        </div>
      </div>
    </div>

    <!-- View 2: Matriz origem × destino -->
    <div v-else-if="hasData && view === 'matrix'" class="tf-matrix-wrap">
      <div class="tf-matrix-scroll">
        <table class="tf-matrix">
          <thead>
            <tr>
              <th class="tf-matrix-corner">de \ para</th>
              <th
                v-for="t in matrixTags"
                :key="'col-' + t"
                class="tf-matrix-col"
                :title="t"
              >
                <span class="tf-matrix-col-name">{{ truncate(t, 14) }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="from in matrixTags" :key="'row-' + from">
              <th class="tf-matrix-row">
                <span class="tf-matrix-row-name" :title="from">{{ truncate(from, 14) }}</span>
              </th>
              <td
                v-for="to in matrixTags"
                :key="'cell-' + from + '-' + to"
                class="tf-matrix-cell"
                :class="{ 'tf-matrix-cell-self': from === to }"
                :style="cellStyle(from, to)"
                :title="cellTooltip(from, to)"
              >
                <span v-if="getCellValue(from, to) > 0">{{ getCellValue(from, to) }}</span>
                <span v-else-if="from === to" class="tf-matrix-dash">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="tf-matrix-legend">
        <span class="tf-leg-label">menos</span>
        <span class="tf-leg-gradient"></span>
        <span class="tf-leg-label">mais</span>
      </div>
    </div>
  </div>
</template>

<script>
const PALETTE = [
  '#556ee6', '#8b5cf6', '#34c38f', '#f1b44c', '#50a5f1',
  '#6366f1', '#0ea5e9', '#a78bfa', '#10b981', '#fbbf24',
  '#ec4899', '#f97316', '#14b8a6', '#d946ef', '#84cc16'
]

export default {
  name: 'transition-flow',
  props: {
    flow: {
      type: Object,
      default: () => ({ nodes: [], links: [], total_transitions: 0, unique_tags: 0 })
    }
  },
  data: () => ({
    view: 'cards',
    viewOptions: [
      { value: 'cards', label: 'Rotas', icon: 'bx bx-list-ul' },
      { value: 'matrix', label: 'Matriz', icon: 'bx bx-grid-alt' }
    ]
  }),
  computed: {
    hasData () {
      return this.flow && this.flow.links && this.flow.links.length > 0
    },
    insights () {
      return (this.flow && this.flow.insights) || []
    },
    topLinks () {
      return [...(this.flow.links || [])].sort((a, b) => b.value - a.value).slice(0, 12)
    },
    matrixTags () {
      // Pega tags ordenadas por volume total envolvido (origem + destino)
      const totals = new Map()
      ;(this.flow.links || []).forEach(l => {
        totals.set(l.source, (totals.get(l.source) || 0) + l.value)
        totals.set(l.target, (totals.get(l.target) || 0) + l.value)
      })
      return Array.from(totals.entries())
        .sort((a, b) => b[1] - a[1])
        .map(e => e[0])
        .slice(0, 12) // matriz cap pra 12×12 — legível em telas normais
    },
    cellLookup () {
      // Map {from→to: value} pra lookup rápido na matriz
      const m = new Map()
      ;(this.flow.links || []).forEach(l => {
        m.set(l.source + '\x00' + l.target, l.value)
      })
      return m
    },
    maxCellValue () {
      let max = 0
      ;(this.flow.links || []).forEach(l => { if (l.value > max) max = l.value })
      return max
    },
    colorByName () {
      // Atribui cores estáveis pelos nomes (consistência com TagFlowGrid)
      const allNames = (this.flow.nodes || []).map(n => n.name)
      const m = new Map()
      allNames.forEach((name, i) => m.set(name, PALETTE[i % PALETTE.length]))
      return m
    }
  },
  methods: {
    colorForName (name) {
      return this.colorByName.get(name) || '#8b90a5'
    },
    rouletteVars (link) {
      return {
        '--src-color': this.colorForName(link.source),
        '--dst-color': this.colorForName(link.target)
      }
    },
    percentOf (link) {
      const total = this.flow.total_transitions || 0
      if (total === 0) return 0
      return Math.round((link.value / total) * 1000) / 10
    },
    getCellValue (from, to) {
      return this.cellLookup.get(from + '\x00' + to) || 0
    },
    cellStyle (from, to) {
      if (from === to) return {}
      const v = this.getCellValue(from, to)
      if (v === 0) return {}
      const intensity = Math.min(1, v / Math.max(1, this.maxCellValue))
      // Gradient roxo (consistente com TagHourHeatmap)
      const stops = [
        [0.0, [240, 242, 250]],
        [0.25, [221, 227, 255]],
        [0.5, [164, 179, 245]],
        [0.75, [110, 127, 230]],
        [1.0, [109, 40, 217]]
      ]
      const c = this.interpolateColor(intensity, stops)
      const textColor = intensity > 0.45 ? '#fff' : '#1a1d26'
      return {
        background: `rgb(${c[0]}, ${c[1]}, ${c[2]})`,
        color: textColor
      }
    },
    interpolateColor (t, stops) {
      for (let i = 0; i < stops.length - 1; i++) {
        const [t0, c0] = stops[i]
        const [t1, c1] = stops[i + 1]
        if (t >= t0 && t <= t1) {
          const f = (t - t0) / (t1 - t0)
          return [
            Math.round(c0[0] + (c1[0] - c0[0]) * f),
            Math.round(c0[1] + (c1[1] - c0[1]) * f),
            Math.round(c0[2] + (c1[2] - c0[2]) * f)
          ]
        }
      }
      return stops[stops.length - 1][1]
    },
    cellTooltip (from, to) {
      if (from === to) return `${from} (mesmo local)`
      const v = this.getCellValue(from, to)
      return v === 0 ? `${from} → ${to}: sem fluxo` : `${from} → ${to}: ${v} pessoa${v === 1 ? '' : 's'}`
    },
    truncate (s, n) {
      if (!s) return ''
      return s.length > n ? s.substring(0, n - 1) + '…' : s
    },
    formatGap (minutes) {
      if (minutes === null || minutes === undefined) return '—'
      if (minutes < 1) return 'menos de 1 min'
      if (minutes < 60) return `${Math.round(minutes * 10) / 10} min`
      const h = Math.floor(minutes / 60)
      const m = Math.round(minutes - h * 60)
      return m === 0 ? `${h}h` : `${h}h ${m}min`
    }
  }
}
</script>

<style lang="scss" scoped>
.tf-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
}

.tf-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.tf-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0 0 4px 0;
}

.tf-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 0;
  max-width: 540px;
}

.tf-toggle {
  display: inline-flex;
  background: #f5f6fa;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 2px;
  gap: 1px;
}

/* Insights narrativos — bloco que vem antes do grid de rotas */
.tf-insights {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
  padding: 14px 16px;
  background: #fafbfc;
  border: 1px solid #eef0f6;
  border-radius: 5px;
}

.tf-insight {
  display: flex;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
  color: #4a5068;
  line-height: 1.5;

  & + .tf-insight {
    padding-top: 8px;
    border-top: 1px solid #f0f1f6;
  }

  strong {
    font-weight: 700;
    color: #1a1d26;
  }
}

.tf-insight-pct {
  font-size: 18px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  min-width: 56px;
  letter-spacing: -0.01em;

  .tf-insight-next_destination & { color: #556ee6; }
  .tf-insight-co_visit & { color: #8b5cf6; }
  .tf-insight-loop_back & { color: #f1b44c; }
}

.tf-insight-text {
  flex: 1;
}

.tf-insight-meta {
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
  margin-left: 6px;
  white-space: nowrap;
}

.tf-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 0;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #4a5068;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.12s ease;

  i { font-size: 14px; }
  &:hover { background: #fff; }

  &.tf-toggle-btn-active {
    background: #fff;
    color: #556ee6;
    box-shadow: 0 1px 3px rgba(26, 29, 38, 0.08);
  }
}

.tf-empty {
  padding: 50px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8b90a5;
  text-align: center;

  .tf-empty-icon {
    font-size: 48px;
    color: #d0d5dd;
    margin-bottom: 10px;
  }
  p { font-size: 13px; font-weight: 600; margin: 0 0 4px 0; color: #4a5068; }
  small { font-size: 12px; max-width: 360px; }
}

/* ─── View 1: Cards de rota ─────────────────────────────────────── */
.tf-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 10px;
}

.tf-route {
  background: #fafbfc;
  border: 1px solid #eef0f6;
  border-radius: 5px;
  padding: 12px 14px;
  position: relative;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(26, 29, 38, 0.06);
    border-color: #d4d8e3;
  }
}

.tf-route-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tf-node {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.tf-node-source { justify-content: flex-start; }
.tf-node-target { justify-content: flex-end; text-align: right; }

.tf-node-label {
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tf-arrow {
  position: relative;
  flex-shrink: 0;
  width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  svg {
    width: 100%;
    height: 14px;
    display: block;
  }
}

.tf-volume {
  font-size: 11px;
  font-weight: 800;
  color: #556ee6;
  font-variant-numeric: tabular-nums;
  margin-top: -2px;
  background: #fff;
  padding: 0 4px;
  border-radius: 4px;
  border: 1px solid #eaecf2;
  line-height: 1.5;
}

.tf-route-foot {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f1f6;
}

.tf-route-pct {
  font-size: 10px;
  font-weight: 600;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ─── View 2: Matriz ───────────────────────────────────────────── */
.tf-matrix-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tf-matrix-scroll {
  overflow-x: auto;
  border: 1px solid #eef0f6;
  border-radius: 10px;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 99px; }
}

.tf-matrix {
  border-collapse: separate;
  border-spacing: 2px;
  font-size: 11px;
  background: #fafbfc;
  padding: 4px;
}

.tf-matrix-corner {
  font-size: 10px;
  font-weight: 700;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: center;
  padding: 6px 10px;
}

.tf-matrix-col,
.tf-matrix-row {
  background: #fff;
  padding: 6px 8px;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
}

.tf-matrix-col {
  text-align: center;
  vertical-align: bottom;
}

.tf-matrix-row {
  text-align: left;
}

.tf-matrix-col-name,
.tf-matrix-row-name {
  font-size: 10px;
  vertical-align: middle;
}

.tf-matrix-cell {
  text-align: center;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: #f0f1f6;
  width: 44px;
  height: 36px;
  border-radius: 4px;
  font-size: 11px;
  cursor: default;
  transition: transform 0.1s ease;

  &:hover {
    transform: scale(1.08);
  }
}

.tf-matrix-cell-self {
  background: repeating-linear-gradient(
    45deg,
    #f0f1f6,
    #f0f1f6 4px,
    #fafbfc 4px,
    #fafbfc 8px
  );
  cursor: not-allowed;

  &:hover { transform: none; }
}

.tf-matrix-dash {
  color: #d0d5dd;
}

.tf-matrix-legend {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-end;
}

.tf-leg-label {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.tf-leg-gradient {
  display: inline-block;
  width: 100px;
  height: 6px;
  border-radius: 99px;
  background: linear-gradient(90deg,
    #f0f2fa 0%, #dde3ff 25%, #a4b3f5 50%, #6e7fe6 75%, #6d28d9 100%
  );
}
</style>
